import { Loader2, RotateCw, X } from 'lucide-react';
import { Badge, Button } from '@ncoder/ui';
import { StatusBadge } from '@/components/atoms/org-admin/StatusBadge';
import type { OrgInvitation } from '@/services/org-admin';

export interface InvitationRowProps {
  invitation: OrgInvitation;
  roleName: string | null;
  isResending: boolean;
  isCancelling: boolean;
  onResend: (invitationId: string) => void;
  onCancel: (invitationId: string) => void;
}

function formatExpiresAt(expiresAt: string): string {
  const date = new Date(expiresAt);
  return Number.isNaN(date.getTime())
    ? expiresAt
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function InvitationRow({
  invitation,
  roleName,
  isResending,
  isCancelling,
  onResend,
  onCancel,
}: InvitationRowProps) {
  const isPending = invitation.status === 'pending';

  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 px-4 py-3 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{invitation.email}</span>
          {roleName && (
            <Badge variant="outline" className="shrink-0 text-xs font-normal">
              {roleName}
            </Badge>
          )}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <StatusBadge status={invitation.status} />
          <span className="text-xs text-muted-foreground">
            Expires {formatExpiresAt(invitation.expires_at)}
          </span>
        </div>
      </div>

      {isPending && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onResend(invitation.id)}
            disabled={isResending || isCancelling}
            title="Resend invitation"
          >
            {isResending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onCancel(invitation.id)}
            disabled={isResending || isCancelling}
            title="Cancel invitation"
            className="text-muted-foreground hover:text-destructive"
          >
            {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
